import {
    AlertCircle,
    ArrowRight,
    BookOpen,
    Calendar,
    Clock,
    Eye,
    EyeOff,
    GraduationCap,
    Heart,
    HelpCircle,
    Loader2,
    Lock,
    Mail,
    MapPin,
    ShieldCheck,
    Star,
    Sun
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { BASE_URL } from '../config/api.js';
import { useAuth } from '../hooks/useAuth.jsx';
import calendarioService from '../services/calendarioService.js';
import './PersonalLogin.css';

const getFullImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${BASE_URL}${path}`;
};

const PersonalLogin = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');
    const { login } = useAuth();

    // Estado para los próximos eventos
    const [upcomingEvents, setUpcomingEvents] = useState([]);
    const [isEventLoading, setIsEventLoading] = useState(true);

    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

    const getIcon = (type) => {
        switch (type) {
            case 'exam': return <AlertCircle size={16} />;
            case 'holiday': return <Sun size={16} />;
            case 'celebration': return <Star size={16} />;
            case 'meeting': return <Clock size={16} />;
            case 'milestone': return <GraduationCap size={16} />;
            case 'birthday': return <Heart size={16} />;
            case 'evaluation': return <BookOpen size={16} />;
            default: return <Calendar size={16} />;
        }
    };

    const getTypeStyle = (type) => {
        switch (type) {
            case 'exam': return 'event-type-exam';
            case 'holiday': return 'event-type-holiday';
            case 'celebration': return 'event-type-celebration';
            case 'meeting': return 'event-type-meeting';
            case 'birthday': return 'event-type-birthday';
            case 'milestone': return 'event-type-milestone';
            case 'evaluation': return 'event-type-evaluation';
            default: return 'event-type-default';
        }
    };

    const formatEventDate = (evento) => {
        if (!evento.fecha) return { month: '', day: '', endDay: '', endMonth: '' };

        const dateObj = new Date(evento.fecha + 'T12:00:00');
        const monthName = months[dateObj.getMonth()];
        const dayStr = String(dateObj.getDate()).padStart(2, '0');

        let endDayStr = '';
        let endMonthName = '';
        if (evento.fecha_fin) {
            const endDateObj = new Date(evento.fecha_fin + 'T12:00:00');
            endDayStr = String(endDateObj.getDate()).padStart(2, '0');
            endMonthName = months[endDateObj.getMonth()];
        }

        return { month: monthName, day: dayStr, endDay: endDayStr, endMonth: endMonthName };
    };

    useEffect(() => {
        // Load remembered user
        const savedUser = localStorage.getItem('rememberedStaffUser');
        if (savedUser) {
            setEmail(savedUser);
            setRememberMe(true);
        }

        // Fetch upcoming events
        const fetchUpcomingEvents = async () => {
            try {
                const events = await calendarioService.obtenerProximosEventosPublicos();
                setUpcomingEvents(Array.isArray(events) ? events.slice(0, 3) : []);
            } catch (err) {
                console.error("No se pudieron cargar los próximos eventos:", err);
            } finally {
                setIsEventLoading(false);
            }
        };

        fetchUpcomingEvents();
    }, []);

    const groupedEvents = upcomingEvents.reduce((acc, event) => {
        const { month } = formatEventDate(event);
        if (!acc[month]) acc[month] = [];
        acc[month].push(event);
        return acc;
    }, {});

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const result = await login(email, password, 'PERSONAL');
            if (result.success) {
                if (rememberMe) {
                    localStorage.setItem('rememberedStaffUser', email);
                } else {
                    localStorage.removeItem('rememberedStaffUser');
                }
                // Force full reload to ensure scripts/styles initialize correctly
                window.location.href = '/';
            } else {
                setError(result.error || 'Credenciales inválidas');
            }
        } catch (err) {
            setError('Error al conectar con el servidor');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="personal-login-wrapper">

            {/* Background */}
            <div className="personal-login-bg">
                <img
                    src="/images/fondo_orionista.jpg"
                    alt="Fondo Institucional"
                />
                <div className="personal-login-overlay-1" />
                <div className="personal-login-overlay-2" />
            </div>

            {/* Main Layout Container */}
            <div className="personal-login-layout-container">

                {/* Glass Box: LOGIN CARD (Independent) */}
                <div className="personal-login-card">

                    {/* Header */}
                    <div className="personal-login-header">
                        <img
                            src="/images/logo_compac.png"
                            alt="Compac Logo"
                            className="personal-login-logo"
                        />
                        <div className="personal-login-badge">
                            <ShieldCheck size={14} style={{ color: '#10b981' }} />
                            Intranet
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="personal-login-form">

                        {error && (
                            <div className="personal-error-glass">
                                <AlertCircle size={18} className="flex-shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        <div className="personal-form-group">
                            <label className="personal-form-label">
                                Correo Electrónico / Usuario
                            </label>
                            <div className="personal-input-container">
                                <div className="personal-input-icon">
                                    <Mail size={20} strokeWidth={1.5} />
                                </div>
                                <input
                                    type="text"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="personal-input-element"
                                    placeholder="usuario@colegio.edu"
                                />
                            </div>
                        </div>

                        <div className="personal-form-group">
                            <div className="personal-form-header">
                                <label className="personal-form-label">
                                    Contraseña
                                </label>
                                <button type="button" className="personal-forgot-link">
                                    ¿Olvidaste tu clave?
                                </button>
                            </div>
                            <div className="personal-input-container">
                                <div className="personal-input-icon">
                                    <Lock size={20} strokeWidth={1.5} />
                                </div>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="personal-input-element"
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="personal-password-toggle"
                                >
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </div>

                        <div className="personal-options">
                            <label className="personal-checkbox-container">
                                <input
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    className="personal-checkbox-input"
                                />
                                <span className="personal-checkbox-label">Mantener sesión iniciada</span>
                            </label>
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="personal-submit-btn"
                        >
                            {isLoading ? (
                                <Loader2 size={20} className="animate-spin" />
                            ) : (
                                <>
                                    Ingresar al sistema
                                    <ArrowRight size={16} />
                                </>
                            )}
                        </button>
                    </form>

                    {/* Footer */}
                    <div className="personal-login-footer">
                        <button type="button" className="personal-help-btn">
                            <HelpCircle size={14} />
                            ¿Necesitas ayuda con tu acceso?
                        </button>
                    </div>
                </div>

                {/* EVENT FEED (Standalone Sibling) */}
                {Object.keys(groupedEvents).length > 0 && (
                    <div className="personal-login-event-feed">
                        <div className="personal-feed-main-header">
                            <span className="feed-badge">COMUNICADOS</span>
                            <h1 className="feed-title">Próximos Eventos</h1>
                            <div className="feed-header-line" />
                        </div>

                        {Object.entries(groupedEvents).map(([month, events]) => (
                            <div key={month} className="login-event-month-section">
                                <div className="login-month-header">
                                    <div className="login-month-icon">
                                        <Calendar size={20} />
                                    </div>
                                    <h2 className="login-month-name">{month}</h2>
                                    <div className="login-month-divider" />
                                </div>

                                <div className="login-events-grid">
                                    {events.map((event) => {
                                        const { day, endDay, endMonth } = formatEventDate(event);
                                        const isFeatured = event.es_destacado; // Solo es largo si está marcado como destacado

                                        return (
                                            <div key={event.id} className={`event-card login-event-card ${isFeatured ? 'featured' : ''} ${getTypeStyle(event.tipo_evento)}`}>
                                                {event.imagen_url && (
                                                    <>
                                                        <div className="event-card-bg">
                                                            <img src={getFullImageUrl(event.imagen_url)} alt={event.titulo} />
                                                        </div>
                                                        <div className="event-card-overlay" />
                                                    </>
                                                )}

                                                <div className="event-card-content">
                                                    <div className="event-card-top">
                                                        <div className="event-type-pills">
                                                            <div className={`event-type-badge ${getTypeStyle(event.tipo_evento)}`}>
                                                                {event.tipo_evento === 'celebration' ? 'Actividad' :
                                                                    event.tipo_evento === 'exam' ? 'Examen' :
                                                                        event.tipo_evento === 'holiday' ? 'Feriado' :
                                                                            event.tipo_evento === 'meeting' ? 'Reunión' :
                                                                                event.tipo_evento === 'birthday' ? 'Cumpleaños' :
                                                                                    event.tipo_evento === 'evaluation' ? 'Eval. Mensual' :
                                                                                        'Hito'}
                                                            </div>
                                                        </div>
                                                        <div className="event-date-box">
                                                            <span className="event-date-month">{month.substring(0, 3).toUpperCase()}</span>
                                                            <span className="event-date-day">
                                                                {day}
                                                                {endDay && endDay !== day && (
                                                                    <span className="date-range-sep"><br /> - {endMonth !== month ? `${endDay} ${endMonth.substring(0, 3)}` : endDay}</span>
                                                                )}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="event-card-bottom">
                                                        <h3 className="event-card-title">{event.titulo}</h3>
                                                        <div className="event-card-meta-container">
                                                            <div className="event-card-meta">
                                                                {getIcon(event.tipo_evento)}
                                                                <span>{event.descripcion}</span>
                                                            </div>
                                                            {(event.hora_inicio || event.ubicacion) && (
                                                                <div className="event-card-details">
                                                                    {event.hora_inicio && (
                                                                        <div className="detail-item">
                                                                            <Clock size={12} />
                                                                            <span>{event.hora_inicio.substring(0, 5)}</span>
                                                                        </div>
                                                                    )}
                                                                    {event.ubicacion && (
                                                                        <div className="detail-item">
                                                                            <MapPin size={12} />
                                                                            <span>{event.ubicacion}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

            </div>
        </div>
    );
};

export default PersonalLogin;
